import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { theme } from "@upsolve/ui";
import styled from "styled-components";

const TOOLTIP_OFFSET = 8;
const VIEWPORT_PADDING = 8;

interface TooltipPosition {
  top: number;
  left: number;
  transformOrigin: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

interface ChatTooltipPortalProps {
  position: TooltipPosition;
  maxWidth?: number;
  children: React.ReactNode;
  role?: string;
  'aria-hidden'?: string;
}

interface ChatTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  delay?: number;
  maxWidth?: number;
}

const TooltipWrapper = styled.div`
  display: inline-block;
`;

const ChatTooltipPortal = styled.div<ChatTooltipPortalProps>`
  position: fixed;
  z-index: 6000;
  padding: 8px 12px;
  background-color: ${theme.colors.gray[900]};
  color: white;
  font-size: 13px;
  border-radius: 6px;
  pointer-events: none;
  max-width: ${({ maxWidth }) => `${maxWidth ?? 280}px`};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  line-height: 1.5;
  word-wrap: break-word;
  transform-origin: ${({ position }) => position.transformOrigin};
  top: ${({ position }) => position.top}px;
  left: ${({ position }) => position.left}px;

  @keyframes tooltipFadeIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  animation: tooltipFadeIn 150ms cubic-bezier(0.16, 1, 0.3, 1);

  &::before {
    content: '';
    position: absolute;
    width: 8px;
    height: 8px;
    background: inherit;
    transform: rotate(45deg);
    
    ${({ position }) => {
      switch (position.placement) {
        case 'top':
          return 'bottom: -4px; left: calc(50% - 4px);';
        case 'bottom':
          return 'top: -4px; left: calc(50% - 4px);';
        case 'left':
          return 'right: -4px; top: calc(50% - 4px);';
        case 'right':
          return 'left: -4px; top: calc(50% - 4px);';
      }
    }}
  }
`;

export const ChatTooltip: React.FC<ChatTooltipProps> = ({
  children,
  content,
  delay = 100,
  maxWidth = 280
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) {
      return null;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Initialize with default position (top)
    let position: TooltipPosition = {
      top: triggerRect.top - tooltipRect.height - TOOLTIP_OFFSET,
      left: triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2),
      transformOrigin: 'bottom center',
      placement: 'top'
    };

    // Check if tooltip would overflow viewport
    if (position.top < VIEWPORT_PADDING) {
      // Try bottom
      position = {
        top: triggerRect.bottom + TOOLTIP_OFFSET,
        left: triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2),
        transformOrigin: 'top center',
        placement: 'bottom'
      };
    }

    // Ensure tooltip stays within horizontal bounds
    position.left = Math.max(
      VIEWPORT_PADDING,
      Math.min(position.left, viewportWidth - tooltipRect.width - VIEWPORT_PADDING)
    );

    return position;
  }, []);

  const updatePosition = useCallback(() => {
    const newPosition = calculatePosition();
    if (newPosition) {
      setPosition(newPosition);
    }
  }, [calculatePosition]);

  const showTooltipWithDelay = () => {
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowTooltip(false);
    setPosition(null);
  };

  useLayoutEffect(() => {
    if (showTooltip) {
      updatePosition();
      
      const handleUpdate = () => {
        requestAnimationFrame(updatePosition);
      };

      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);

      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [showTooltip, updatePosition]);

  return (
    <TooltipWrapper
      ref={triggerRef}
      onMouseEnter={showTooltipWithDelay}
      onMouseLeave={hideTooltip}
      onFocus={showTooltipWithDelay}
      onBlur={hideTooltip}
    >
      {children}
      {showTooltip && createPortal(
        <ChatTooltipPortal
          ref={tooltipRef}
          position={position || { top: 0, left: 0, transformOrigin: 'center', placement: 'top' }}
          maxWidth={maxWidth}
          role="tooltip"
          aria-hidden="false"
        >
          {content}
        </ChatTooltipPortal>,
        document.body
      )}
    </TooltipWrapper>
  );
};